import React, { useState, useEffect } from 'react';
import { 
  Award,
  Search,
  DollarSign,
  CheckCircle,
  XCircle,
  Info,
  TrendingUp,
  Users,
  Clock,
  Gift,
  History,
  User,
  Mail,
  Calendar,
  Coins,
  AlertTriangle,  
  RefreshCw      
} from 'lucide-react';
import UsersService from './home/homeservice';

const RewardPointsAward = () => {
  const [loading, setLoading] = useState(false);
  const [awarding, setAwarding] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Award state with dropdown
  const [awardForm, setAwardForm] = useState({
    points_amount: '',
    reason_type: '',
    custom_reason: ''
  });

  // Predefined reasons for awarding points (ranked by importance)
  const predefinedReasons = [
    { value: 'referring', label: 'Referring new members' },
    { value: 'investments', label: 'Making investments' },
    { value: 'social_sharing', label: 'Sharing the app on social media' },
    { value: 'event_invites', label: 'Inviting friends to events/webinars' },
    { value: 'event_attendance', label: 'Attending meetings/events' },
    { value: 'discussions', label: 'Contributing to group discussions' },
    { value: 'training', label: 'Completing in-app training/tutorials' },
    { value: 'feedback', label: 'Providing feedback/reviews' },
    { value: 'bug_reports', label: 'Reporting bugs or suggesting features' },
    { value: 'surveys', label: 'Participating in surveys/polls' },
    { value: 'loan_repayment', label: 'Timely loan repayment' },
    { value: 'campaigns', label: 'Joining seasonal/promotional campaigns' },
    { value: 'services', label: 'Using in-app services' },
    { value: 'security', label: 'Offering security advice (cybercrime awareness)' },
    { value: 'royal_upgrade', label: 'Upgrading to Royal Family membership' },
    { value: 'biography', label: 'Maintaining a top-rated "My Desire" biography' },
    { value: 'daily_login', label: 'Daily app login' },
    { value: 'challenges', label: 'Engaging in community challenges' },
    { value: 'testimonials', label: 'Sharing success stories/testimonials' },
    { value: 'custom', label: 'Custom (specify below)' }
  ];
  
  // History state
  const [history, setHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Statistics state
  const [stats, setStats] = useState({
    total_awards: 0,
    total_points_awarded: 0
  });

  // Search users
  const handleSearchUsers = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }

    setSearching(true);
    setError(null);
    setSearchResults([]);
    
    try {
      const response = await UsersService.searchUsersForAward(searchQuery);
      setSearchResults(response.users || []);
      
      if (response.users.length === 0) {
        setError(`No users found matching "${searchQuery}"`);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      const errorMessage = typeof error === 'string' ? error : 
                          error.message || 
                          error.detail || 
                          'Failed to search users';
      setError(errorMessage);
    } finally {
      setSearching(false);
    }
  };

  // Handle user selection
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchResults([]);
    setSearchQuery('');
    setError(null);
  };

  // Award points
  const handleAwardPoints = async (e) => {
    e.preventDefault();
    
    if (!selectedUser) {
      setError('Please select a user first');
      return;
    }

    if (!awardForm.points_amount || parseFloat(awardForm.points_amount) <= 0) {
      setError('Please enter a valid points amount');
      return;
    }

    if (!awardForm.reason_type) {
      setError('Please select a reason for awarding points');
      return;
    }

    if (awardForm.reason_type === 'custom' && !awardForm.custom_reason.trim()) {
      setError('Please provide a custom reason');
      return;
    }

    setAwarding(true);
    setError(null);
    setSuccess(null);

    try {
      // Get the final reason text
      const finalReason = awardForm.reason_type === 'custom' 
        ? awardForm.custom_reason 
        : predefinedReasons.find(r => r.value === awardForm.reason_type)?.label;

      const response = await UsersService.awardPointsToUser(
        selectedUser.email,
        parseFloat(awardForm.points_amount),
        finalReason
      );

      setSuccess(response.message || 'Points awarded successfully!');
      
      // Reset state
      setAwardForm({
        points_amount: '',
        reason_type: '',
        custom_reason: ''
      });
      setSelectedUser(null);
      
      // Reload history
      if (showHistory) {
        loadAwardHistory();
      }
      
    } catch (error) {
      console.error('Error awarding points:', error);
      const errorMessage = typeof error === 'string' ? error : 
                          error.errors || 
                          error.message || 
                          error.detail || 
                          'Failed to award points';
      setError(JSON.stringify(errorMessage));
    } finally {
      setAwarding(false);
    }
  };

  // Load award history
  const loadAwardHistory = async (page = 1) => {
    setLoadingHistory(true);
    setError(null);

    try {
      const response = await UsersService.getPointsAwardHistory(
        page,
        historyPagination.pageSize
      );
      
      setHistory(response.awards || []);
      setHistoryPagination({
        page: response.pagination.page,
        pageSize: response.pagination.page_size,
        total: response.pagination.total,
        totalPages: response.pagination.total_pages
      });
      
      if (response.summary) {
        setStats({
          total_awards: response.summary.total_awards,
          total_points_awarded: response.summary.total_points_awarded
        });
      }
      
    } catch (error) {
      console.error('Error loading history:', error);
      const errorMessage = typeof error === 'string' ? error : 
                          error.message || 
                          error.detail || 
                          'Failed to load award history';
      setError(errorMessage);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle Enter key in search
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchUsers();
    }
  };

  // Auto-hide messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Load history when tab is opened
  useEffect(() => {
    if (showHistory && history.length === 0) {
      loadAwardHistory();
    }
  }, [showHistory]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-8xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Gift className="h-8 w-8 text-orange-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Reward Points Award</h1>
              <p className="text-gray-600">Manually award points to users</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-8xl mx-auto px-6 py-6">
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
              <span className="text-green-800">{success}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-center">
              <XCircle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-4">
            <button
              onClick={() => setShowHistory(false)}
              className={`pb-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                !showHistory
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4" />
                Award Points
              </div>
            </button>
            <button
              onClick={() => setShowHistory(true)}
              className={`pb-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                showHistory
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <History className="h-4 w-4" />
                Award History
              </div>
            </button>
          </nav>
        </div>

        {!showHistory ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <Award className="h-5 w-5 text-yellow-600" />
                  Award Points to User
                </h2>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Step 1: Search for User
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Search by name, email, or ID..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        disabled={searching}
                      />
                    </div>
                    <button
                      onClick={handleSearchUsers}
                      disabled={searching || !searchQuery.trim()}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {searching ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4" />
                          Search
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Search by user's name, email address, or ID number
                  </p>
                </div>

                {searchResults.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Results ({searchResults.length})
                    </label>
                    <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto">
                      {searchResults.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => handleSelectUser(user)}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center">
                                <User className="h-5 w-5 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{user.name || 'N/A'}</p>
                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {user.email}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900">
                                {user.current_reward_points.toLocaleString()} pts
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatCurrency(user.wallet_balance)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedUser && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Step 2: Selected User
                    </label>
                    <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-12 bg-orange-200 rounded-full flex items-center justify-center">
                            <User className="h-6 w-6 text-orange-700" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{selectedUser.name || 'N/A'}</p>
                            <p className="text-sm text-gray-600">{selectedUser.email}</p>
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <CheckCircle className="h-3 w-3" />
                              {selectedUser.is_verified ? 'Verified' : 'Unverified'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-700">Current Points</p>
                          <p className="text-2xl font-bold text-orange-600">
                            {selectedUser.current_reward_points.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedUser(null)}
                        className="mt-3 text-sm text-orange-700 hover:text-orange-800 font-medium"
                      >
                        Change User
                      </button>
                    </div>
                  </div>
                )}

                {selectedUser && (
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Step 3: Points Amount
                      </label>
                      <div className="relative">
                        <Coins className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={awardForm.points_amount}
                          onChange={(e) => setAwardForm({
                            ...awardForm,
                            points_amount: e.target.value
                          })}
                          placeholder="Enter points amount"
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        New balance will be: {(parseFloat(selectedUser.current_reward_points) + (parseFloat(awardForm.points_amount) || 0)).toLocaleString()} points
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Step 4: Reason for Award
                      </label>
                      <select
                        value={awardForm.reason_type}
                        onChange={(e) => setAwardForm({
                          ...awardForm,
                          reason_type: e.target.value,
                          custom_reason: ''
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        required
                      >
                        <option value="">-- Select a reason --</option>
                        {predefinedReasons.map((reason) => (
                          <option key={reason.value} value={reason.value}>
                            {reason.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Select the action that earned the reward points
                      </p>
                    </div>

                    {awardForm.reason_type === 'custom' && (
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom Reason
                        </label>
                        <textarea
                          value={awardForm.custom_reason}
                          onChange={(e) => setAwardForm({
                            ...awardForm,
                            custom_reason: e.target.value
                          })}
                          placeholder="Enter your custom reason for awarding points..."
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Provide a clear explanation for this custom reward
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-end gap-3 mt-6">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUser(null);
                          setAwardForm({ points_amount: '', reason_type: '', custom_reason: '' });
                        }}
                        className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAwardPoints}
                        disabled={
                          awarding || 
                          !awardForm.points_amount || 
                          !awardForm.reason_type ||
                          (awardForm.reason_type === 'custom' && !awardForm.custom_reason.trim())
                        }
                        className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {awarding ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Awarding...
                          </>
                        ) : (
                          <>
                            <Gift className="h-4 w-4" />
                            Award Points
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {!selectedUser && searchResults.length === 0 && (
                  <div className="text-center py-12">
                    <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Search for a user to get started</p>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-1 space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-800 mb-2">How to Award Points</h4>
                    <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                      <li>Search for the user by name, email, or ID</li>
                      <li>Select the user from search results</li>
                      <li>Enter the points amount to award</li>
                      <li>Select a reason from the dropdown menu</li>
                      <li>If custom reason, provide details</li>
                      <li>Click "Award Points" to confirm</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-orange-600" />
                  Quick Statistics
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-xs text-gray-600">Total Awards</span>
                    <span className="text-sm font-bold text-gray-900">
                      {stats.total_awards.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-xs text-gray-600">Points Awarded</span>
                    <span className="text-sm font-bold text-orange-600">
                      {stats.total_points_awarded.toLocaleString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowHistory(true)}
                  className="w-full mt-3 text-sm text-orange-600 hover:text-orange-700 font-medium"
                >
                  View Full History →
                </button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800 mb-2">Important Notes</h4>
                    <ul className="text-xs text-yellow-700 space-y-1">
                      <li>• All awards are logged and auditable</li>
                      <li>• Points are added immediately to user balance</li>
                      <li>• Always provide a clear, detailed reason</li>
                      <li>• Awards cannot be undone once confirmed</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-100 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <History className="h-5 w-5 text-orange-600" />
                  Award History
                </h2>
                <button
                  onClick={() => loadAwardHistory(historyPagination.page)}
                  disabled={loadingHistory}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors"
                >
                  <RefreshCw className={`h-4 w-4 ${loadingHistory ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-orange-50 rounded-lg p-3">
                  <p className="text-xs text-orange-700 mb-1">Total Awards</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {stats.total_awards.toLocaleString()}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-green-700 mb-1">Total Points</p>
                  <p className="text-2xl font-bold text-green-900">
                    {stats.total_points_awarded.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-3"></div>
                    <p className="text-gray-500">Loading history...</p>
                  </div>
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No award history yet</p>
                </div>
              ) : (
                <>
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Recipient
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Points
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reason
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Awarded By
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {history.map((award) => (
                        <tr key={award.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                                <User className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {award.recipient.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {award.recipient.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              +{award.points_awarded.toLocaleString()} pts
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-900 line-clamp-2">
                              {award.reason}
                            </p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <p className="text-sm text-gray-900">{award.awarded_by}</p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-500">
                              <Calendar className="h-4 w-4 mr-1" />
                              {new Date(award.transaction_date).toLocaleDateString()}
                            </div>
                            <div className="flex items-center text-xs text-gray-400">
                              <Clock className="h-3 w-3 mr-1" />
                              {new Date(award.transaction_date).toLocaleTimeString()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {historyPagination.totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Page {historyPagination.page} of {historyPagination.totalPages}
                        <span className="ml-2 text-gray-500">
                          ({historyPagination.total} total awards)
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => loadAwardHistory(historyPagination.page - 1)}
                          disabled={historyPagination.page === 1 || loadingHistory}
                          className="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => loadAwardHistory(historyPagination.page + 1)}
                          disabled={historyPagination.page >= historyPagination.totalPages || loadingHistory}
                          className="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardPointsAward;